/**
 * A content-key wrapped to one recipient — a directed disclosure envelope (Milestone 13 / roadmap §20.3).
 *
 * Phase 1 distributed the [Capability] (the symmetric content-key) out of band. A disclosure instead boxes it with
 * [IdentityBox] to one recipient's Ed25519 identity, authored by the discloser. This is the only new type the
 * disclosure layer needs — it converts a directed wrap back into the Phase-1 [Capability], so the resolution-gating
 * path ([SealedContent] + [CapabilityResolver]) is unchanged.
 *
 * **It is a transport-layer artifact, not a graph node.** A wrap has exactly one consumer (the recipient), is spent
 * the moment that recipient decodes the key, and is regenerable by any holder in microseconds — so it is delivered as
 * a directed envelope (addressed-to-peerId, best-effort) and is never replicated, stored, or custodied (§20.1 custody
 * covers only the ciphertext). "Membership = a set of wraps" is deliberately *not* modeled: a decentralized system
 * has no authoritative reader set, only the act of a disclosure.
 *
 * The §20.3 properties fall out of "disclosure = forwarding":
 * - **Admission** = a key-holder authors a wrap to a recipient (the existing `recommend`/`relay` act + this
 *   envelope). **"Removal"** is a non-event: no one authors a further wrap.
 * - **Forward secrecy by construction**: with no wrap, the recipient cannot derive the key for content sealed later.
 * - **Only a holder can disclose onward**: only a key-holder can author a valid wrap (roadmap §18.1), so the
 *   disclosure boundary is enforced by who holds the key, not by any label.
 * - **No strict leak-tracing** (DRM-class impossible, roadmap §20.2); the wrap stays *authenticated* to its author.
 */
package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.identity.Identity
import com.oasisfeng.invaluable.identity.IdentityBox
import com.oasisfeng.invaluable.identity.SecretBox
import kotlinx.io.bytestring.ByteString

/**
 * A content-key boxed to a single recipient's identity, authenticated to [author] and **bound to the one
 * [contentProjection] it opens** (review P1).
 *
 * The boxed plaintext is `contentProjection.payload || capability.key`, so the projection the discloser intends this
 * key for is authenticated by the same Poly1305 tag that authenticates the key. This turns "Alice disclosed *this
 * content* to Bob" from an application-layer proximity fact (Bob saw an Alice-authored wrap *near* a route) into a
 * protocol fact: [open] recovers the key only if the recovered payload still equals the carried [contentProjection],
 * so a wrap cannot be re-pointed at another content and a tampered/stale [contentProjection] field fails closed.
 *
 * The projection *scheme* is not boxed (it rides the transport carrier and is re-attached from the local store's
 * single scheme, mirroring the [LineWireCodec] payload-only convention); only the scheme-free `payload` — the content
 * digest, the security-relevant bytes — is authenticated. A wrong scheme simply matches no held seal, which is inert.
 *
 * @property author The discloser's X.509 SPKI public key — the identity this wrap is authenticated to.
 * @property nonce The [IdentityBox] nonce.
 * @property wrap `tag || ciphertext` of `contentProjection.payload || content-key`, boxed to the recipient.
 * @property contentProjection The sealed content this key opens; its `payload` is authenticated inside [wrap].
 */
data class CapabilityWrap(
    val author: ByteString,
    val nonce: ByteString,
    val wrap: ByteString,
    val contentProjection: LinkProjection,
) {

    /**
     * Recover the [Capability] if [recipient] is the addressee whose private key opens this wrap **and** the wrap was
     * authored for this [contentProjection]; null otherwise (not the addressee, tampered ciphertext, or a
     * [contentProjection] field that no longer matches the authenticated payload).
     */
    fun open(recipient: Identity): Capability? {
        val plain = IdentityBox.unwrap(wrap.toByteArray(), author.toByteArray(), recipient.privateKeyBytes, nonce.toByteArray())
            ?: return null
        if (plain.size < SecretBox.KEY_SIZE) return null
        val split = plain.size - SecretBox.KEY_SIZE                     // payload || key; the key is a fixed-size tail
        val boundPayload = ByteString(*plain.copyOfRange(0, split))
        if (boundPayload != contentProjection.payload) return null      // the projection was altered after sealing
        return Capability(ByteString(*plain.copyOfRange(split, plain.size)))
    }

    companion object {

        /**
         * Wrap [capability] for [contentProjection] to [recipientPublicKeySpki] (the recipient's X.509 SPKI public
         * key), authored by [author]. The projection's payload is bound into the box (see [open]).
         */
        fun of(
            capability: Capability,
            contentProjection: LinkProjection,
            recipientPublicKeySpki: ByteString,
            author: Identity,
            nonce: ByteString,
        ): CapabilityWrap = CapabilityWrap(
            author = ByteString(*author.publicKeySPKI),
            nonce = nonce,
            wrap = ByteString(
                *IdentityBox.wrap(
                    contentProjection.payload.toByteArray() + capability.key.toByteArray(),
                    recipientPublicKeySpki.toByteArray(),
                    author.privateKeyBytes,
                    nonce.toByteArray(),
                ),
            ),
            contentProjection = contentProjection,
        )
    }
}
